import {ApolloError, gql} from '@apollo/client';
import {getTestProps} from '@benchling/js-test-utils';
import {Badge, Button, createStyles, Group, Stack, Tabs, Title} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import React, {useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import CanvasPreviewFrame from 'canvas-core/preview/CanvasPreviewFrame';
import {CanvasPreviewErrorReason} from 'canvas-core/preview/previewErrors';
import useCanvasExpandedPreviewUrl from 'canvas-core/preview/useCanvasExpandedPreviewUrl';
import * as NavConstants from 'constants/NavConstants';
import {getApolloClient} from 'graphql-client/ApolloClient';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  archiveAgentFoundry,
  ArchiveAgentFoundryResult,
  unarchiveAgentFoundry,
} from 'llm/chat-library/foundries/AgentFoundriesDAO';
import AgentFoundryAccessModal from 'llm/chat-library/foundries/AgentFoundryAccessModal';
import AgentFoundryAgents from 'llm/chat-library/foundries/AgentFoundryAgents';
import AgentFoundryChatButton from 'llm/chat-library/foundries/AgentFoundryChatButton';
import AgentFoundryDetails from 'llm/chat-library/foundries/AgentFoundryDetails';
import AgentFoundryNeedsAttention, {
  countOpenHumanActionItems,
} from 'llm/chat-library/foundries/AgentFoundryNeedsAttention';
import AgentFoundryTimeline from 'llm/chat-library/foundries/AgentFoundryTimeline';
import ArchiveAgentFoundryModal from 'llm/chat-library/foundries/ArchiveAgentFoundryModal';
import {AGENT_FOUNDRIES_QUERY} from 'llm/chat-library/foundries/useAgentFoundries';
import {openExistingChat} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import notifications from 'notifications';
import EntityStore from 'stores/EntityStore';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import MantineOverflowMenu from 'ui-library/menus/MantineOverflowMenu';
import {CenteredSpinner} from 'ui-library/Spinner';
import {getUserMessage, isServerRequestError} from 'util/Net';

import {
  AgentFoundryQuery as AgentFoundryQueryData,
  AgentFoundryQuery_agentFoundry_AgentFoundryNode as AgentFoundryData,
  AgentFoundryQuery_agentFoundry_AgentFoundryNode_coordinatorAgent_RoutineNode as FoundryRoutine,
  AgentFoundryQueryVariables,
} from './__generated/AgentFoundryQuery';

// A valid archive purpose for every object type; matches what archiving an agent uses.
const ARCHIVE_REASON = 'Retired';

function pluralizeAgents(count: number): string {
  return count === 1 ? '1 agent' : `${count} agents`;
}

export function getArchiveSummary(foundryName: string, result: ArchiveAgentFoundryResult): string {
  const sentences = [`Archived ${foundryName}.`];
  if (result.archivedAgentIds.length > 0) {
    sentences.push(`Archived ${pluralizeAgents(result.archivedAgentIds.length)}.`);
  }
  if (result.pausedAgentIds.length > 0) {
    sentences.push(`Paused ${pluralizeAgents(result.pausedAgentIds.length)}.`);
  }
  if (result.skippedAgentIds.length > 0) {
    sentences.push(
      `${pluralizeAgents(result.skippedAgentIds.length)} left unchanged because you don't have permission to change them.`
    );
  }
  return sentences.join(' ');
}

// Whether the foundry records a Coordinator, which `coordinatorAgent` can't tell apart from an
// unreadable or deleted one since both resolve to null. Reads the legacy `foreman_agent_id` key too,
// matching the server's fallback.
function hasCoordinatorAgent(agentFoundry: AgentFoundryData): boolean {
  const configuration = agentFoundry.configuration as {
    coordinator_agent_id?: string | null;
    foreman_agent_id?: string | null;
  };
  return (configuration.coordinator_agent_id ?? configuration.foreman_agent_id) != null;
}

// The Coordinator plus the assigned agents, with an agent that fills both roles listed once. A null
// entry is an agent the viewer cannot read.
function getFoundryRoutines(agentFoundry: AgentFoundryData): Array<FoundryRoutine | null> {
  const routines = [agentFoundry.coordinatorAgent, ...agentFoundry.agents.map((entry) => entry.agent)];
  const includesCoordinator = hasCoordinatorAgent(agentFoundry);
  const seenIds = new Set<string>();
  return routines.filter((routine, index) => {
    if (routine == null) {
      return index > 0 || includesCoordinator;
    }
    if (seenIds.has(routine.id)) {
      return false;
    }
    seenIds.add(routine.id);
    return true;
  });
}

// Overview, Needs attention, Agents, and Details are static tabs. A foundry's additional canvas tabs are dynamic,
// so they are keyed by system name instead (see `additionalCanvasTabValue`).
enum AgentFoundryTab {
  AGENTS = 'AGENTS',
  CANVAS = 'CANVAS',
  DETAILS = 'DETAILS',
  NEEDS_ATTENTION = 'NEEDS_ATTENTION',
}

// The system name of the Overview canvas, as understood by the canvas render endpoint.
const OVERVIEW_CANVAS_TAB_NAME = 'overview';

// Prefixed so a system name can never collide with a static tab's value.
function additionalCanvasTabValue(systemName: string): string {
  return `canvas-tab:${systemName}`;
}

const useStyles = createStyles((theme) => ({
  container: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing.md,
    height: '100%',
    minHeight: 0,
    padding: theme.spacing.xl,
  },
  tabsRoot: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
  },
  canvasPanel: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
    paddingTop: theme.spacing.md,
  },
  needsAttentionPanel: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    paddingTop: theme.spacing.md,
  },
  agentsPanel: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    paddingTop: theme.spacing.md,
  },
  detailsPanel: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    paddingTop: theme.spacing.md,
  },
  timelineSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.sm,
    paddingTop: theme.spacing.xl,
    paddingBottom: theme.spacing.md,
  },
}));

// The routine fields the agents roster needs to render a row (name, schedule, status) are
// selected directly on `coordinatorAgent`/`agents.agent` here rather than via a separate
// routinesByIds lookup: both are already typed Routine references on AgentFoundry, so there is no
// separate id-resolution step to save a round trip for.
export const AGENT_FOUNDRY_QUERY = gql`
  query AgentFoundryQuery($id: StrictString!) @engComponent(name: LLM) @skipAudit {
    agentFoundry(id: $id) {
      __typename
      ... on AgentFoundryNode {
        id
        name
        description
        createdAt
        modifiedAt
        creatorName
        archiveData {
          archived
        }
        permissions {
          archivable
        }
        configuration
        coordinatorDescription
        coordinatorAgent {
          id
          name
          triggerType
          triggerConfig
          status
          archiveData {
            archived
          }
          permissions {
            archivable
            writable
          }
        }
        # Selects beyond id so that a dangling or unreadable chat reference resolves to null
        # (an id-only selection would echo the stored id back even if the chat is gone).
        activeCoordinatorChat {
          id
          name
        }
        agents {
          description
          agent {
            id
            name
            triggerType
            triggerConfig
            status
            archiveData {
              archived
            }
            permissions {
              archivable
              writable
            }
          }
        }
        additionalCanvasTabs {
          systemName
          displayName
        }
        humanActionItems {
          itemId
          title
          description
          urlPath
          category
          isDone
          assignee {
            id
            name
            avatarUrl
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface AgentFoundryCanvasProps {
  agentFoundryId: string;
  canvasTabDisplayName: string;
  canvasTabName: string;
  onResumeChat?: () => void;
}

/**
 * One of the canvases backing an agent foundry, filling whatever height its container gives it. Reuses
 * useCanvasExpandedPreviewUrl and CanvasPreviewFrame -- the same signed-URL fetch and
 * iframe/sandbox setup chat-embedded canvases use (see ChatCanvasArtifactView) -- but renders only
 * the canvas contents, without any of that view's card chrome (icon tile, version badge, refresh
 * button), since this page already has its own header.
 *
 * Fetches by `agentFoundryId` so the render endpoint can authorize on the foundry itself, letting
 * collaborators without direct canvas-version access view it too. `canvasTabName` selects which of
 * the foundry's canvases to render: 'overview' or an additional tab's system name. A missing canvas
 * explains how to resume setup, while other render failures surface their preview error.
 */
function AgentFoundryCanvas({
  agentFoundryId,
  canvasTabDisplayName,
  canvasTabName,
  onResumeChat,
}: AgentFoundryCanvasProps): JSX.Element {
  const {signedUrl, isLoading, errorMessage, errorReason} = useCanvasExpandedPreviewUrl({
    agentFoundryId,
    agentFoundryCanvasTabName: canvasTabName,
  });

  if (isLoading) {
    return <CenteredSpinner />;
  }

  if (errorMessage != null) {
    if (errorReason === CanvasPreviewErrorReason.AGENT_FOUNDRY_CANVAS_NOT_CONFIGURED) {
      return (
        <InlineBanner
          bannerType={InlineBannerType.ERROR}
          text={`No ${canvasTabDisplayName} yet`}
          supportingInfo={
            onResumeChat == null
              ? "Setup stopped before the Coordinator built this foundry's canvas."
              : "Setup stopped before the Coordinator built this foundry's canvas. Resume the chat to finish it."
          }
          action={
            onResumeChat == null
              ? undefined
              : {
                  actionType: InlineBannerActionType.CUSTOM,
                  actionText: 'Resume chat',
                  actionOnClick: onResumeChat,
                  buttonStyle: ButtonStyle.PRIMARY,
                  rightAlign: true,
                }
          }
          {...getTestProps({component: 'AgentFoundryPage', element: 'noCanvasBanner'})}
        />
      );
    }
    return (
      <InlineBanner
        bannerType={InlineBannerType.ERROR}
        text={errorMessage}
        {...getTestProps({component: 'AgentFoundryPage', element: 'noCanvasBanner'})}
      />
    );
  }

  if (signedUrl == null) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text="Unable to load the canvas." />;
  }

  return <CanvasPreviewFrame signedUrl={signedUrl} />;
}

/**
 * Detail view for a single Agent Foundry, rendered as its own workspace tab (see BufferUtil and
 * EditorRoutes.agentFoundryEditor) and addressable at its own URL (see AIRoutes): the foundry's name,
 * a "Chat" button for talking to the Coordinator (see AgentFoundryChatButton), a "Manage access"
 * button that opens AgentFoundryAccessModal, and tabs for the
 * canvases currently backing it (Overview, then Needs attention, then any additional canvas tabs, in
 * order) and the agents provisioned for it, followed by its details and description. The Manage access button is shown
 * to any viewer who can load this page -- like RoutineAccessTab, the modal it opens renders
 * read-only for anyone without canManageCollaborators, rather than being hidden outright.
 */
export default function AgentFoundryPage({agentFoundryId}: {agentFoundryId: string}): JSX.Element {
  const {classes} = useStyles();
  const [isAccessModalOpen, {open: openAccessModal, close: closeAccessModal}] = useDisclosure(false);
  const [isArchiveModalOpen, {open: openArchiveModal, close: closeArchiveModal}] = useDisclosure(false);
  const [isUnarchiving, setIsUnarchiving] = useState(false);
  const queryResult = useBenchlingQuery<AgentFoundryQueryData, AgentFoundryQueryVariables>(
    AGENT_FOUNDRY_QUERY,
    // Keeps the page (and the selected tab) mounted while the action items are reloaded after one is marked done.
    {variables: {id: agentFoundryId}, renderCachedDataWhenRefetching: true}
  );

  if (queryResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  }

  if (
    queryResult.type === QueryResultType.ERROR ||
    queryResult.data.agentFoundry == null ||
    isInaccessible(queryResult.data.agentFoundry)
  ) {
    return (
      <Stack spacing="md" className={classes.container}>
        <InlineBanner
          bannerType={InlineBannerType.ERROR}
          text="This foundry could not be found, or you do not have access to it."
        />
      </Stack>
    );
  }

  const {agentFoundry} = queryResult.data;
  const openHumanActionItemCount = countOpenHumanActionItems(agentFoundry.humanActionItems);
  // Action items are optional: the Coordinator may convey what needs a human's attention some other way
  // (for example in a canvas), in which case there is no tab at all.
  const hasActionItems = agentFoundry.humanActionItems.length > 0;
  const isArchived = agentFoundry.archiveData.archived;
  const canArchive = agentFoundry.permissions.archivable === true;
  const activeCoordinatorChatId = agentFoundry.activeCoordinatorChat?.id;
  const handleResumeChat =
    activeCoordinatorChatId == null
      ? undefined
      : (): void => {
          openExistingChat(activeCoordinatorChatId);
          SidepanelActions.unexpandToSection(NavConstants.SECTIONS.AI);
        };

  // Doesn't reject on a failed query: the change has already been saved, so a failed refresh must not
  // be reported as a failure to archive or unarchive.
  async function refetchFoundries(): Promise<void> {
    try {
      await getApolloClient().refetchQueries({include: [AGENT_FOUNDRY_QUERY, AGENT_FOUNDRIES_QUERY]});
    } catch (err: unknown) {
      if (!(err instanceof ApolloError)) {
        throw err;
      }
      notifications.error('Could not refresh this foundry. Reload the page to see the latest changes.');
    }
  }

  async function handleArchive({archiveAgents}: {archiveAgents: boolean}): Promise<void> {
    const result = await archiveAgentFoundry(agentFoundryId, {reason: ARCHIVE_REASON, archiveAgents});
    const summary = getArchiveSummary(agentFoundry.name, result);
    if (result.skippedAgentIds.length > 0) {
      notifications.warn(summary);
    } else {
      notifications.success(summary);
    }
    await refetchFoundries();
  }

  async function handleUnarchive(): Promise<void> {
    setIsUnarchiving(true);
    try {
      await unarchiveAgentFoundry(agentFoundryId);
    } catch (err: unknown) {
      setIsUnarchiving(false);
      if (isServerRequestError(err)) {
        notifications.error(getUserMessage(err) || 'Could not unarchive this foundry.');
        return;
      }
      throw err;
    }
    notifications.success(
      `Unarchived ${agentFoundry.name}. Its agents stay paused or archived until you resume or unarchive them.`
    );
    await refetchFoundries();
    setIsUnarchiving(false);
  }

  return (
    <div className={classes.container} {...getTestProps({component: 'AgentFoundryPage', element: 'root'})}>
      {isArchived && (
        <InlineBanner
          bannerType={InlineBannerType.ATTENTION}
          text="This foundry is archived."
          action={
            canArchive && !isUnarchiving
              ? {
                  actionType: InlineBannerActionType.CUSTOM,
                  actionText: 'Unarchive',
                  actionOnClick: handleUnarchive,
                }
              : undefined
          }
          dataTestAttrs={{component: 'AgentFoundryPage', element: 'archivedBanner'}}
        />
      )}
      <Group position="apart" align="flex-start" noWrap={true}>
        <Title order={3}>{agentFoundry.name}</Title>
        <Group spacing="sm" noWrap={true}>
          <AgentFoundryChatButton
            coordinatorAgentId={agentFoundry.coordinatorAgent?.id ?? null}
            activeCoordinatorChatId={agentFoundry.activeCoordinatorChat?.id ?? null}
          />
          <Button
            variant="secondary"
            onClick={openAccessModal}
            {...getTestProps({component: 'AgentFoundryPage', element: 'manageAccessButton'})}
          >
            Manage access
          </Button>
          {canArchive && !isArchived && (
            <MantineOverflowMenu
              sections={[
                {
                  items: [
                    {
                      name: 'Archive',
                      icon: 'archive',
                      variant: 'destructive',
                      onSelect: openArchiveModal,
                      dataTestAttrs: {component: 'AgentFoundryPage', element: 'archiveMenuItem'},
                    },
                  ],
                },
              ]}
            />
          )}
        </Group>
      </Group>
      <Tabs defaultValue={AgentFoundryTab.CANVAS} keepMounted={false} className={classes.tabsRoot}>
        <Tabs.List>
          <Tabs.Tab
            value={AgentFoundryTab.CANVAS}
            {...getTestProps({component: 'AgentFoundryPage', element: 'tab', key: 'Overview'})}
            data-amp-safe={false}
          >
            Overview
          </Tabs.Tab>
          {hasActionItems && (
            <Tabs.Tab
              value={AgentFoundryTab.NEEDS_ATTENTION}
              rightSection={
                openHumanActionItemCount > 0 ? (
                  <Badge
                    size="sm"
                    variant="filled"
                    {...getTestProps({component: 'AgentFoundryPage', element: 'needsAttentionCount'})}
                  >
                    {openHumanActionItemCount}
                  </Badge>
                ) : undefined
              }
              {...getTestProps({component: 'AgentFoundryPage', element: 'tab', key: 'Needs attention'})}
              data-amp-safe={false}
            >
              Needs attention
            </Tabs.Tab>
          )}
          {agentFoundry.additionalCanvasTabs.map((tab) => (
            <Tabs.Tab
              key={tab.systemName}
              value={additionalCanvasTabValue(tab.systemName)}
              {...getTestProps({component: 'AgentFoundryPage', element: 'tab', key: tab.systemName})}
              data-amp-safe={false}
            >
              {tab.displayName}
            </Tabs.Tab>
          ))}
          <Tabs.Tab
            value={AgentFoundryTab.AGENTS}
            {...getTestProps({component: 'AgentFoundryPage', element: 'tab', key: 'Agents'})}
            data-amp-safe={false}
          >
            Agents
          </Tabs.Tab>
          <Tabs.Tab
            value={AgentFoundryTab.DETAILS}
            {...getTestProps({component: 'AgentFoundryPage', element: 'tab', key: 'Details'})}
            data-amp-safe={false}
          >
            Details
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value={AgentFoundryTab.CANVAS} className={classes.canvasPanel}>
          <AgentFoundryCanvas
            agentFoundryId={agentFoundryId}
            canvasTabDisplayName="Overview"
            canvasTabName={OVERVIEW_CANVAS_TAB_NAME}
            onResumeChat={handleResumeChat}
          />
        </Tabs.Panel>

        {hasActionItems && (
          <Tabs.Panel value={AgentFoundryTab.NEEDS_ATTENTION} className={classes.needsAttentionPanel}>
            <AgentFoundryNeedsAttention
              agentFoundryId={agentFoundryId}
              actionItems={agentFoundry.humanActionItems}
              currentUserId={EntityStore.currentUser()?.id ?? null}
              onActionItemDoneChanged={queryResult.refetch}
            />
          </Tabs.Panel>
        )}

        {agentFoundry.additionalCanvasTabs.map((tab) => (
          <Tabs.Panel
            key={tab.systemName}
            value={additionalCanvasTabValue(tab.systemName)}
            className={classes.canvasPanel}
          >
            <AgentFoundryCanvas
              agentFoundryId={agentFoundryId}
              canvasTabDisplayName={tab.displayName}
              canvasTabName={tab.systemName}
              onResumeChat={handleResumeChat}
            />
          </Tabs.Panel>
        ))}

        <Tabs.Panel value={AgentFoundryTab.AGENTS} className={classes.agentsPanel}>
          <AgentFoundryAgents
            coordinatorAgent={agentFoundry.coordinatorAgent}
            coordinatorDescription={agentFoundry.coordinatorDescription}
            agents={agentFoundry.agents}
          />
          {BenchlingConfig.LLM_ENABLE_FOUNDRY_TIMELINE && (
            <div className={classes.timelineSection}>
              <Title order={4}>Activity timeline</Title>
              <AgentFoundryTimeline agentFoundryId={agentFoundryId} />
            </div>
          )}
        </Tabs.Panel>

        <Tabs.Panel value={AgentFoundryTab.DETAILS} className={classes.detailsPanel}>
          <AgentFoundryDetails
            createdAt={agentFoundry.createdAt}
            creatorName={agentFoundry.creatorName}
            description={agentFoundry.description}
            id={agentFoundry.id}
            modifiedAt={agentFoundry.modifiedAt}
          />
        </Tabs.Panel>
      </Tabs>
      {isAccessModalOpen && (
        <AgentFoundryAccessModal agentFoundryId={agentFoundryId} onRequestClose={closeAccessModal} />
      )}
      {isArchiveModalOpen && (
        <ArchiveAgentFoundryModal
          opened={true}
          foundryName={agentFoundry.name}
          agents={getFoundryRoutines(agentFoundry)}
          onRequestClose={closeArchiveModal}
          onRequestSubmit={handleArchive}
        />
      )}
    </div>
  );
}
